/**
 * Priority Main layout for single-button relays and one-channel PM stacks.
 * npm test src/utest/sidepanel/relay_priority_layout.test.ts
 */

import { predefinedEntries } from 'src/helpers/quick_control_helper';
import { convertToSeconds } from 'src/helpers/timer_helpers';

export const RELAY_PRIORITY_SINGLE_SWITCH_TYPES = [
	'SNSW-001X16EU',
	'SNSW-001X15UL',
	'SNSW-devX16EU',
	'SNSW-001X8EU',
	'S3SW-001X16EU',
	'S3SW-001X8EU',
	'S4SW-001X16EU',
	'S4SW-001X8EU',
	'SPSW-001XE16EU',
	'SPSW-101XE16EU',
	'SPSW-201XE16EU',
	'SPSW-201XE15UL',
] as const;

export const RELAY_DEV_TIMER_REMAINING_SEC = 75;

/** Matches `compute-device-state` uiState.type used for overlay inference. */
export const RELAY_UI_OFFLINE = 0;

const OVERLAY_MUTE_ACCENT = 'var(--color-theme-fg-02)';
const OVERLAY_HOT_ACCENT = 'var(--color-theme-climate-hot)';

export type RelayPriorityDeviceType = (typeof RELAY_PRIORITY_SINGLE_SWITCH_TYPES)[number];

export type RelayPriorityOverlay = 'offline' | 'overpower' | 'overheat' | 'overvoltage';

export type RelayPriorityDevForce = 'auto' | 'on' | 'off' | 'timer' | RelayPriorityOverlay;

export type RelayCloseChrome = 'label' | 'icon';

export type RelayPriorityCopy = {
	key: string;
	default: string;
};

export type RelayPriorityDockTone = 'muted' | 'warning' | 'danger';

export type RelayPriorityDockCard = {
	id: string;
	value: RelayPriorityCopy;
	numericValue?: string;
	unit?: string;
	label: RelayPriorityCopy;
	icon: string;
	priority: 'primary';
	tone: RelayPriorityDockTone;
	accentColor?: string;
	kind: 'status' | 'metric';
};

export type RelayPriorityMeterCard = {
	id: 'apower' | 'voltage' | 'current';
	value: string;
	unit: string;
	label: RelayPriorityCopy;
	icon: string;
	priority: 'primary' | 'secondary';
	kind: 'metric';
};

export type RelayPmReadings = {
	apower: number;
	voltage: number;
	current: number;
	temperature?: number;
};

export type RelayQuickTimerOption = {
	id: string;
	seconds: number;
	hr: string;
	min: string;
	sec: string;
};

export type RelayPriorityDevOption = {
	id: string;
	label: string;
};

export type RelaySwitchState = {
	ison: boolean;
	hasTimer: boolean;
	timerStarted?: number;
};

export type RelayPriorityLayout = RelaySwitchState & {
	timerRemaining?: number;
	overlay: RelayPriorityOverlay | null;
	dockCard: RelayPriorityDockCard | null;
};

const SINGLE_SWITCH_TYPE_SET = new Set<string>(RELAY_PRIORITY_SINGLE_SWITCH_TYPES);

const OVERLAY_FORCES: RelayPriorityOverlay[] = ['offline', 'overpower', 'overheat', 'overvoltage'];

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
	value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

const asNumber = (value: unknown): number | undefined =>
	typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const asTemperatureC = (value: unknown): number | undefined => {
	const direct = asNumber(value);
	if (direct != null) return direct;
	const record = asRecord(value);
	return record ? asNumber(record.tC) : undefined;
};

const formatWatts = (value: number): { value: string; unit: string } => {
	const sign = value < 0 ? '-' : '';
	let next = Math.abs(value);
	let unit = 'W';
	if (next > 999) {
		next = next / 1000;
		unit = 'kW';
	}
	const rounded = Math.round(next * 100) / 100;
	return { value: `${sign}${rounded}`, unit };
};

const formatFixed = (value: number): string => {
	const rounded = Math.round(value * 100) / 100;
	return `${rounded}`;
};

const normalizeError = (error: string): string => {
	const colonIndex = error.indexOf(':');
	if (colonIndex === -1) return error;
	const suffix = error.slice(colonIndex + 1);
	return /^\d+$/.test(suffix) ? error.slice(0, colonIndex) : error;
};

const pushErrors = (target: string[], value: unknown) => {
	if (Array.isArray(value)) {
		value.forEach((item) => {
			if (typeof item === 'string' && item) target.push(normalizeError(item));
		});
		return;
	}
	if (typeof value === 'string' && value) target.push(normalizeError(value));
};

export const isRelayOverlayForce = (
	force: RelayPriorityDevForce | undefined,
): force is RelayPriorityOverlay =>
	Boolean(force && OVERLAY_FORCES.includes(force as RelayPriorityOverlay));

export const isRelayPrioritySingleSwitch = (deviceType: string | undefined): boolean =>
	Boolean(deviceType && SINGLE_SWITCH_TYPE_SET.has(deviceType));

/** One-channel PM relays that stack Active power under the ControlCard island. */
export const RELAY_PRIORITY_PM_METER_TYPES = [
	'SNSW-001P16EU',
	'SNSW-001P15UL',
	'SNSW-001P8EU',
	'S3SW-001P16EU',
	'S3SW-001P8EU',
	'S3SW-001P16SP',
	'S4SW-001P16EU',
	'S4SW-001P8EU',
	'SPSW-001PE16EU',
	'SPSW-101PE16EU',
	'SPSW-201PE16EU',
	'SPSW-201PE15UL',
	'SPSW-001PE40EU',
	'SNSL-0A1X03WWL',
	'S3PB-O3AR000001',
] as const;

const PM_METER_TYPE_SET = new Set<string>(RELAY_PRIORITY_PM_METER_TYPES);

/** One-channel PM relays show live power / voltage / current under the switch. */
export const isRelayPriorityPmMeter = (deviceType: string | undefined): boolean =>
	Boolean(deviceType && PM_METER_TYPE_SET.has(deviceType));

/** Whether this device type wraps the Main switch island in Design System ControlCard. */
export const isRelayPriorityControlCard = (deviceType: string | undefined): boolean =>
	isRelayPrioritySingleSwitch(deviceType) || isRelayPriorityPmMeter(deviceType);

export const relayQuickTimerOptions = (): RelayQuickTimerOption[] =>
	predefinedEntries().map((entry) => {
		const hr = entry.hr || '00';
		return {
			id: `${hr}-${entry.min}-${entry.sec}`,
			seconds: convertToSeconds(hr, entry.min, entry.sec),
			hr,
			min: entry.min,
			sec: entry.sec,
		};
	});

export const relayCountdownCopy = (ison: boolean): RelayPriorityCopy =>
	ison
		? { key: 'label_turning_off_in', default: 'Turning off in' }
		: { key: 'label_turning_on_in', default: 'Turning on in' };

export const relayActNowCopy = (ison: boolean): RelayPriorityCopy =>
	ison
		? { key: 'button_turn_off_now', default: 'Turn off now' }
		: { key: 'button_turn_on_now', default: 'Turn on now' };

export const relayPriorityFlipKey = (input: {
	ison: boolean;
	hasTimer: boolean;
	picking: boolean;
	timerStarted?: number;
}): string =>
	`${input.ison ? 'on' : 'off'}:${input.hasTimer ? 'timer' : 'idle'}:${input.picking ? 'picking' : 'rail'}:${input.timerStarted ?? 0}`;

export const RELAY_PRIORITY_DEV_OPTIONS: RelayPriorityDevOption[] = [
	{ id: 'auto', label: 'Auto' },
	{ id: 'on', label: 'On' },
	{ id: 'off', label: 'Off' },
	{ id: 'timer', label: 'Timer' },
	{ id: 'offline', label: 'Offline' },
	{ id: 'overpower', label: 'Overpower' },
	{ id: 'overheat', label: 'Overheat' },
	{ id: 'overvoltage', label: 'Overvoltage' },
];

export const RELAY_OFFLINE_DOCK_CARD: RelayPriorityDockCard = {
	id: 'connection',
	value: { key: 'label_relay_overlay_offline', default: 'Offline' },
	label: {
		key: 'label_relay_overlay_offline_hint',
		default: 'Device unavailable',
	},
	icon: 'fa-solid fa-cloud-slash',
	priority: 'primary',
	tone: 'muted',
	accentColor: OVERLAY_MUTE_ACCENT,
	kind: 'status',
};

const gen1ChannelRecord = (
	status: Record<string, unknown> | undefined,
	key: 'relays' | 'meters',
	channelIndex: number,
): Record<string, unknown> | undefined => {
	const list = status?.[key];
	return Array.isArray(list) ? asRecord(list[channelIndex]) : undefined;
};

export const collectRelayProtectionErrors = (
	status: Record<string, unknown> | undefined,
	component: string,
	channelIndex = 0,
): string[] => {
	const errors: string[] = [];
	pushErrors(errors, asRecord(status?.[component])?.errors);
	if (!component) pushErrors(errors, gen1ChannelRecord(status, 'relays', channelIndex)?.errors);
	if (status?.overtemperature) errors.push('overtemperature');
	return errors;
};

export const inferRelayOverlay = ({
	online = true,
	uiStateType,
	force,
	errors = [],
}: {
	online?: boolean;
	uiStateType?: number;
	force?: RelayPriorityDevForce;
	errors?: string[];
}): RelayPriorityOverlay | null => {
	if (isRelayOverlayForce(force)) return force;
	if (!online || uiStateType === RELAY_UI_OFFLINE) return 'offline';

	const codes = new Set(errors.map((error) => error.toLowerCase()));
	const has = (...needles: string[]) => needles.some((needle) => codes.has(needle));

	if (has('overpower')) return 'overpower';
	if (has('overtemp', 'overtemperature', 'overheat')) return 'overheat';
	if (has('overvoltage')) return 'overvoltage';
	return null;
};

export const buildRelayDockCard = (
	overlay: RelayPriorityOverlay,
	readings?: RelayPmReadings,
): RelayPriorityDockCard => {
	if (overlay === 'offline') return RELAY_OFFLINE_DOCK_CARD;

	if (overlay === 'overheat') {
		const temperature = readings?.temperature ?? 78.4;
		return {
			id: 'temperature',
			value: { key: 'label_emeter_overheating', default: 'Overheating' },
			numericValue: formatFixed(temperature),
			unit: '°C',
			label: { key: 'label_emeter_overheating', default: 'Overheating' },
			icon: 'fa-solid fa-temperature-half',
			priority: 'primary',
			tone: 'warning',
			accentColor: OVERLAY_HOT_ACCENT,
			kind: 'metric',
		};
	}

	if (overlay === 'overvoltage') {
		return {
			id: 'voltage',
			value: { key: 'label_emeter_overvoltage', default: 'Overvoltage' },
			numericValue: formatFixed(readings?.voltage ?? 0),
			unit: 'V',
			label: { key: 'label_emeter_overvoltage', default: 'Overvoltage' },
			icon: 'fa-solid fa-wave-square',
			priority: 'primary',
			tone: 'warning',
			kind: 'metric',
		};
	}

	const formatted = formatWatts(readings?.apower ?? 0);
	return {
		id: 'apower',
		value: { key: 'label_emeter_overpower', default: 'Overpower' },
		numericValue: formatted.value,
		unit: formatted.unit,
		label: { key: 'label_emeter_overpower', default: 'Overpower' },
		icon: 'fa-solid fa-bolt',
		priority: 'primary',
		tone: 'danger',
		kind: 'metric',
	};
};

const withOverlay = (
	layout: RelaySwitchState & { timerRemaining?: number },
	overlay: RelayPriorityOverlay | null,
	readings?: RelayPmReadings,
): RelayPriorityLayout => ({
	...layout,
	overlay,
	dockCard: overlay ? buildRelayDockCard(overlay, readings) : null,
});

export const RELAY_CLOSE_CHROME_DEV_OPTIONS: RelayPriorityDevOption[] = [
	{ id: 'label', label: 'Close label' },
	{ id: 'icon', label: 'Close icon' },
];

export const readRelaySwitchState = (
	status: Record<string, unknown> | undefined,
	component: string,
	channelIndex = 0,
): RelaySwitchState => {
	if (component) {
		const channel = asRecord(status?.[component]);
		const ison = Boolean(channel?.output ?? channel?.on);
		const timerStarted = asNumber(channel?.timer_started_at);
		return {
			ison,
			hasTimer: timerStarted != null,
			...(timerStarted != null ? { timerStarted } : {}),
		};
	}
	const relay = gen1ChannelRecord(status, 'relays', channelIndex);
	const ison = Boolean(relay?.ison);
	const timerStarted = asNumber(relay?.timer_started);
	const hasTimer = Boolean(relay?.has_timer) || timerStarted != null;
	return {
		ison,
		hasTimer,
		...(timerStarted != null ? { timerStarted } : {}),
	};
};

export const readRelayApower = (
	status: Record<string, unknown> | undefined,
	component: string,
): number => asNumber(asRecord(status?.[component])?.apower) ?? 0;

export const readRelayPmReadings = (
	status: Record<string, unknown> | undefined,
	component: string,
	channelIndex = 0,
): RelayPmReadings => {
	if (component) {
		const channel = asRecord(status?.[component]);
		const temperature =
			asTemperatureC(status?.['temperature:0']) ?? asTemperatureC(channel?.temperature);
		return {
			apower: asNumber(channel?.apower) ?? 0,
			voltage: asNumber(channel?.voltage) ?? 0,
			current: asNumber(channel?.current) ?? 0,
			...(temperature != null ? { temperature } : {}),
		};
	}
	const meter = gen1ChannelRecord(status, 'meters', channelIndex);
	const temperature = asTemperatureC(status?.tmp) ?? asTemperatureC(status?.temperature);
	return {
		apower: asNumber(meter?.power) ?? asNumber(meter?.apower) ?? 0,
		voltage: asNumber(meter?.voltage) ?? asNumber(status?.voltage) ?? 0,
		current: asNumber(meter?.current) ?? 0,
		...(temperature != null ? { temperature } : {}),
	};
};

export const buildRelayPmMeterCard = (apower: number): RelayPriorityMeterCard => {
	const formatted = formatWatts(apower);
	return {
		id: 'apower',
		value: formatted.value,
		unit: formatted.unit,
		label: { key: 'label_emeter_active_power', default: 'Active power' },
		icon: 'fa-solid fa-bolt',
		priority: 'primary',
		kind: 'metric',
	};
};

export const buildRelayPmMeterCards = (readings: RelayPmReadings): RelayPriorityMeterCard[] => [
	buildRelayPmMeterCard(readings.apower),
	{
		id: 'voltage',
		value: formatFixed(readings.voltage),
		unit: 'V',
		label: { key: 'label_voltage', default: 'Voltage' },
		icon: 'fa-solid fa-wave-square',
		priority: 'secondary',
		kind: 'metric',
	},
	{
		id: 'current',
		value: formatFixed(readings.current),
		unit: 'A',
		label: { key: 'label_emeter_current', default: 'Current' },
		icon: 'fa-solid fa-gauge-high',
		priority: 'secondary',
		kind: 'metric',
	},
];

export const relaySwitchTimerDisplay = (input: {
	hasTimer: boolean;
	remaining?: number;
	percentage?: number;
}): { time: number; percentage: number } => {
	if (!input.hasTimer) return { time: 0.001, percentage: 100 };
	return {
		time: Math.max(input.remaining ?? 0, 0.001),
		percentage: Math.min(100, Math.max(0, input.percentage ?? 100)),
	};
};

export const buildRelayPriorityLayout = (input: {
	ison: boolean;
	hasTimer: boolean;
	timerStarted?: number;
	timerRemaining?: number;
	online?: boolean;
	uiStateType?: number;
	force?: RelayPriorityDevForce;
	errors?: string[];
	readings?: RelayPmReadings;
}): RelayPriorityLayout => {
	const overlay = inferRelayOverlay({
		online: input.online,
		uiStateType: input.uiStateType,
		force: input.force,
		errors: input.errors,
	});

	if (input.force === 'on') {
		return withOverlay({ ison: true, hasTimer: false }, overlay, input.readings);
	}
	if (input.force === 'off') {
		return withOverlay({ ison: false, hasTimer: false }, overlay, input.readings);
	}
	if (input.force === 'timer') {
		return withOverlay(
			{
				ison: false,
				hasTimer: true,
				timerRemaining: input.timerRemaining ?? RELAY_DEV_TIMER_REMAINING_SEC,
			},
			overlay,
			input.readings,
		);
	}

	return withOverlay(
		{
			ison: input.ison,
			hasTimer: input.hasTimer,
			...(input.timerStarted != null ? { timerStarted: input.timerStarted } : {}),
			...(input.hasTimer ? { timerRemaining: input.timerRemaining } : {}),
		},
		overlay,
		input.readings,
	);
};
